import {
  InstallmentSettleFormValues,
  InstallmentSettleSingleRequest,
  SettleInstallmentSingleData,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { Button, Form, Modal, ModalProps } from 'antd';
import moment from 'moment';
import React, { useEffect } from 'react';
import SettleSingleAccountPayableForm from './SettleSingleAccountPayableForm';
import { useInstallmentSettledSingle } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { DateTimeFormat } from '@/utils/moment';
import { AxiosError } from 'axios';
import { handleInstallmentAlreadyEdited } from '@/core/domain/financial/AccountPayable/AccountPayable.errors';

interface Props extends Omit<ModalProps, 'onCancel'> {
  data: SettleInstallmentSingleData;
  onCancel: VoidFunction;
}

const SettledSingleModal: React.FC<Props> = ({ onCancel, open, data }) => {
  const [form] = Form.useForm<InstallmentSettleFormValues>();

  const today = moment().format('YYYY-MM-DD');
  const payDate = Form.useWatch('payDate', form);

  const isEarlierDate = moment(payDate).isBefore(today);

  const settleSingleMutation = useInstallmentSettledSingle();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data, payDate: moment() });
    }
  }, [data]);

  const handleSettleSingleInstallment = async () => {
    try {
      const values = await form.validateFields();

      if (data && data.id && data.installmentPayableId) {
        const obj: InstallmentSettleSingleRequest = {
          ...values,
          id: data.id,
          installmentPayableId: data.installmentPayableId,
          netValue: data.installmentValue ?? 0,
          payDate: values.payDate.format(DateTimeFormat),
        };

        await settleSingleMutation.mutateAsync(obj);

        handleCancel();
      }
    } catch (err: unknown) {
      const error = err as AxiosError;

      if (error.response?.data) {
        if (handleInstallmentAlreadyEdited(error)) {
          handleCancel();
        }
      }
    }
  };

  function handleCancel() {
    form.resetFields();
    onCancel();
  }

  return (
    <Modal
      width={400}
      open={open}
      onCancel={handleCancel}
      title="Liquidar Parcela"
      footer={[
        <Button type="primary" ghost onClick={onCancel}>
          Cancelar
        </Button>,
        <Button
          disabled={
            isEarlierDate &&
            !hasPermissions([
              Authorities.FINANCIAL_ACCOUNT_PAYABLE_SETTLE_REFUND_WITH_PREVIOUS_DATE,
            ])
          }
          type="primary"
          loading={settleSingleMutation.isLoading}
          onClick={handleSettleSingleInstallment}
        >
          Liquidar
        </Button>,
      ]}
      destroyOnClose
    >
      <SettleSingleAccountPayableForm form={form} data={data} />
    </Modal>
  );
};

export default SettledSingleModal;
